// 2026-09-01 신규 — 상단바 우측 등급 배지(상시 진입점 3안 확정).
// 2026-09-02 — 열세 상태 배선: 판정과 툴팁 문장은 `src/lib/tierCardCopy.js`(`buildTierView`)에서
// 온다. 승인 직후(등급 행 없음)부터 루키 링이 뜬다 — 전엔 행이 있어야 보였다.
//
// 홈이 아닌 모든 페이지에 남는 유일한 등급 요소다. 28px 원형 링 안에 등급 심볼을 넣고, 링의
// 채워진 각도가 곧 다음 등급까지의 진행(루키는 작품 영상 N/5)이다. 세로 공간을 쓰지 않는다.
//
// **여기서 카드를 펼치지 않는다.** 클릭하면 곧장 `/my-tier`. 데스크톱은 올려두면 툴팁 두 줄
// (등급·상태 / 다음 한 걸음)만 보여준다. 햄버거 메뉴의 등급 카드(`TierDropdownCard`)는 제거됐다.
//
// 등급 비대상 계정에서는 훅이 summary=null 을 주므로 아무것도 렌더하지 않는다.
//
// 2026-09-02 — 루키·휴면 배지 가시성 수정(사용자 지적 "등급 표시가 실버 이상만 있는 것 같다").
// 실측(상태별 정적 렌더 하네스, `TierNavBadge.test.jsx`)으로 원인을 확인했다: 배지는 루키·휴면·
// 승인 직후(행 없음)에서도 **렌더는 되고 있었지만**, 진행도 0인 루키(0/5)·승인 직후·휴면 전부가
// "연회색 트랙(neutral.200) + 면 0개의 옅은 점선 육각형"만 남아 흰 상단바에서 빈 자리로 읽혔다.
// 정식 등급은 등급 고유색 호(진행도)와 채워진 면이 있어 보이고, 루키는 1편이라도 올려야 틸 호가
// 생겨서 "실버 이상만 보인다"는 인상이 정확히 맞았다. 판정 로직·훅·뷰모델은 문제 없음.
// 고친 것: 링 **트랙**을 상태별로 그린다 — 정식 등급은 기존 그대로(neutral.200 실선), 루키 계열은
// 브랜드 틸 **점선** 트랙(TierSymbol 루키 심볼의 점선 외곽과 같은 시각 언어 — "여정 진행 중"),
// 휴면은 neutral.400 **실선** 트랙("멈춤", ChannelTierBadge 의 회색 휴면 배지와 같은 중립 톤).
// 진행도 호(브랜드 틸 실선)는 그 위에 그대로 얹힌다. 크기·클릭·툴팁·심볼은 바꾸지 않았다.
// 이 표현은 구현자 판단이다 — 대표/알파 검수 대상(`docs/pages/main/Home.md` 2026-09-02 항목).
import { useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import ActionTooltip from "./ActionTooltip";
import { useNavigate } from "react-router-dom";
import TierSymbol from "./TierSymbol";
import colors from "../theme/foundations/colors";
import { tt } from "../i18n/tt";
import useTierView from "../hooks/useTierView";

const TIER_COLOR_KEY = {
  실버: "silver",
  골드: "gold",
  플래티넘: "platinum",
  다이아몬드: "diamond",
  마스터: "master",
};

const SIZE = 28;
const STROKE = 2.5;

export default function TierNavBadge({ summary }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const view = useTierView(summary);   // 자정·창이 닫히는 때 다시 만든다(137차)
  if (!view) return null;

  // 루키는 등급 고유색이 회색(#9a948a)이라 링이 배경에 묻힌다 — "진행 중"을 뜻하는 브랜드
  // 틸을 쓴다. 휴면은 목표가 없으므로 링을 채우지 않는다.
  // 2026-09-02 — 정식 출신 휴면(view.dormant)도 브랜드 틸로 통일. view.tier가 실제 등급명이
  // 아니라 DORMANT_LABEL이라(휴면 표시 정책) TIER_COLOR_KEY 조회가 원래도 실패해 rookie 색으로
  // 폴백했겠지만, 의도를 명시하려고 조건을 직접 추가했다.
  const ringColor = view.isRookie || view.dormant
    ? colors.brand[500]
    : (colors.tier[TIER_COLOR_KEY[view.tier]] || colors.tier.rookie).base;
  const percent = view.dormant ? 0 : view.ringPercent;

  const r = (SIZE - STROKE) / 2;
  const circumference = 2 * Math.PI * r;

  // 트랙(배경 링) — 상태별. 위 2026-09-02 주석 참고. `data-tier-track` 은 테스트가 상태를 읽는 손잡이.
  const track = view.dormant
    ? { kind: "dormant", stroke: colors.neutral[400], dash: undefined }
    : view.isRookie
      ? { kind: "rookie", stroke: colors.brand[300], dash: "3 3" }
      : { kind: "tier", stroke: colors.neutral[200], dash: undefined };

  return (
    <ActionTooltip
      label={
        <Box py={0.5}>
          <Text fontSize="xs" fontWeight="700" lineHeight="1.45">{view.tooltip.title}</Text>
          {view.tooltip.sub && (
            <Text fontSize="xs" color="whiteAlpha.800" lineHeight="1.45">{view.tooltip.sub}</Text>
          )}
        </Box>
      }
      placement="bottom-end"
      openDelay={200}
      bg="neutral.800"
      borderRadius="7px"
      px={3}
      py={2}
      hasArrow={false}
    >
      <Flex
        as="button"
        type="button"
        aria-label={tt("a.df0c931d", "내 등급 {{v0}} · {{v1}}", { v0: view.tooltip.title, v1: view.tooltip.sub })}
        align="center"
        p="3px"
        borderRadius="99px"
        bg={hovered ? "neutral.50" : "transparent"}
        border="1px solid"
        borderColor={hovered ? "neutral.200" : "transparent"}
        transition="background .15s ease, border-color .15s ease"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => navigate("/my-tier")}
      >
        <Box position="relative" w={`${SIZE}px`} h={`${SIZE}px`}>
          <svg
            width={SIZE}
            height={SIZE}
            style={{ display: "block", transform: "rotate(-90deg)" }}
            aria-hidden="true"
            data-tier-track={track.kind}
          >
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={r}
              fill="none"
              stroke={track.stroke}
              strokeWidth={STROKE}
              strokeDasharray={track.dash}
              strokeLinecap={track.dash ? "round" : undefined}
            />
            {percent > 0 && (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={r}
                fill="none"
                stroke={ringColor}
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={`${(circumference * percent) / 100} ${circumference}`}
              />
            )}
          </svg>
          <Flex position="absolute" inset={0} align="center" justify="center">
            {/* 2026-09-02 — 휴면(정식 출신 포함)은 동결된 실제 등급 심볼을 그리지 않는다(휴면
                표시 정책). HomeTierCard.jsx의 Symbol과 동일한 이유. */}
            {view.isRookie || view.dormant ? (
              <TierSymbol isRookie size={15} decorative />
            ) : (
              <TierSymbol tierName={view.tier} size={15} decorative />
            )}
          </Flex>
        </Box>
      </Flex>
    </ActionTooltip>
  );
}
